/* eslint-disable import/order */
import React, { Component } from 'react'
import Immutable from 'immutable'
import { Collapse, Radio, Select, Tag, Tooltip } from 'antd'
import { InfoCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { immutableSelector } from '@/utils'
import { InputNumber, Upload } from '@watchface/components'
import { SELECT_STYLE } from '@watchface/constants/style'
import T from '@watchface/components/I18n'
import { TOOLTIP_COLOR } from '@watchface/constants'

import { renderCells } from '@watchface/utils/render'
import { VERTEX_DOT_LINE_STYLE } from '@watchface/constants/graph'
import { getEditableDisplayComponentKey, getWatchEditMode, WATCH_EDIT_MODE, WATCH_EDIT_MODE_KEY, getSelectedEditableParentKey } from './state'

const { Option } = Select
const { Panel } = Collapse

class EditableComponent extends Component {
  static keyMap = {}

  constructor(props) {
    super(props)
    const { allData, parentKey } = props
    const path = parentKey.split('_')
    const component = immutableSelector(allData.getIn([...path]))
    const { select_list } = immutableSelector(
      component
    )
    this.state = {
      showSelectListVisible: !!select_list
    }
  }

  componentDidMount() {
    this.preloadTextTagDimensions()
    this.preloadSelectImageDimensions()
  }

  componentDidUpdate(prevProps) {
    const { allData, parentKey } = this.props
    // Preload text tag dimensions only when its image changes.
    if (allData !== prevProps.allData) {
      const path = parentKey.split('_')
      const prevTextTag = prevProps.allData.getIn([...path, 'text_tag'])
      const curTextTag = allData.getIn([...path, 'text_tag'])
      if (prevTextTag?.get('image') !== curTextTag?.get('image')) {
        this.preloadTextTagDimensions()
      }
      const prevSelectImage = prevProps.allData.getIn([...path, 'select_image'])
      const curSelectImage = allData.getIn([...path, 'select_image'])
      if (prevSelectImage !== curSelectImage) {
        this.preloadSelectImageDimensions()
      }
    }

    if (prevProps !== this.props) {
      // Mode switch is handled by the mode effect; only render for other prop changes
      const prevMode = EditableComponent.getWatchPreviewMode(prevProps)
      const curMode = EditableComponent.getWatchPreviewMode(this.props)
      if (prevMode === curMode) {
        EditableComponent.renderLayer(this.props)
      }
    }
  }

  static renderLayer = (param) => {
    const keyMap = renderCells({
      com: EditableComponent,
      ...param
    })
    EditableComponent.keyMap = Object.assign(EditableComponent.keyMap, keyMap)
    const { parentKey, dialType, allAssets, allData, menu, CONFIG_MAP, keyPrefix, editableComponentKey } = param
    if (!CONFIG_MAP) return
    const { componentMap } = CONFIG_MAP
    const path = parentKey.split('_')
    const rawComponentData = allData.getIn([...path])
    const previewValue = EditableComponent.getRenderValue({
      allAssets,
      dialType,
      path,
      menu,
      parentKey,
      componentData: rawComponentData
    })
    const checkedMenu = EditableComponent.getCheckedMenu({ menu, parentKey })
    if (previewValue) {
      checkedMenu.forEach((item) => {
        const { key, children = Immutable.List() } = immutableSelector(item)
        children.forEach((child) => {
          const { key: childKey, checked } = immutableSelector(child)
          const { cls, constMap = Immutable.Map(), propMap = {} } = componentMap(dialType)[key]
          const prop = propMap[childKey] || {}
          if (checked && previewValue === key) {
            cls.renderLayer({
              ...param,
              data: allData.getIn([...path, 'props', key, 'children', childKey]) || Immutable.Map(),
              assets: allAssets.getIn([dialType, ...path, 'props', key, 'children', childKey]) || Immutable.Map(),
              childKey,
              parentKey: key,
              editableComponentKey,
              keyPrefix: `${keyPrefix}-${key}`,
              prefixPath: [...path, 'props'],
              constMap,
              ...prop
            })
          } else {
            cls.removeLayer({ ...param, childKey, parentKey: key, keyPrefix: `${keyPrefix}-${key}`, constMap, ...prop })
          }
        })
      })
    } else {
      checkedMenu.forEach((item) => {
        const { key, children = Immutable.List() } = immutableSelector(item)
        children.forEach((child) => {
          const { key: childKey } = immutableSelector(child)
          const { cls, propMap = {} } = componentMap(dialType)[key]
          const prop = propMap[childKey] || {}
          cls.removeLayer({ ...param, childKey, parentKey: key, keyPrefix: `${keyPrefix}-${key}`, ...prop })
        })
      })
    }
  }

  static getCheckedMenu = ({ menu, parentKey }) => {
    const item = menu.find((item) => item.get('key') === parentKey) || Immutable.Map()
    const children = item.get('children') || Immutable.List()
    return children.filter((child) => child.get('checked'))
  }

  static getCellDatas = ({ allData, allAssets, parentKey, dialType }) => {
    const cells = {
      imageEntity: {},
      vertexEntity: {}
    }

    const mode = EditableComponent.getWatchPreviewMode({ allAssets, dialType })

    const path = parentKey.split('_')
    const rectangle = allData.getIn([...path, 'rectangle']) || Immutable.Map()
    const { position, width: rectWidth, height: rectHeight } = immutableSelector(rectangle)

    // Edit-only decorations: selection image, tag image, and dashed rectangle.
    if (mode === WATCH_EDIT_MODE.EDIT) {
      const selectStateImage = EditableComponent.getSelectStateImage({ allData, allAssets, parentKey, dialType })
      if (selectStateImage) {
        cells.imageEntity[selectStateImage.key] = selectStateImage
      }

      const tagImage = EditableComponent.getTagImage({ allData, allAssets, parentKey, dialType })
      if (tagImage) {
        cells.imageEntity[tagImage.key] = tagImage
      }

      if (rectWidth && rectHeight) {
        const { x, y } = immutableSelector(position)
        cells.vertexEntity.selectRect = {
          key: 'selectRect',
          left: x,
          top: y,
          width: rectWidth,
          height: rectHeight,
          type: 'rect',
          fill: null,
          zIndex: 19999,
          ...VERTEX_DOT_LINE_STYLE
        }
      }
    }

    // Component preview images are rendered in both edit and runtime modes.
    const images = EditableComponent.getComponentPreviewImages({ allData, allAssets, parentKey, dialType })
    images.forEach((image) => {
      const { key } = image
      cells.imageEntity[key] = image
    })

    return cells
  }

  static getWatchPreviewMode = ({ allAssets, dialType }) => {
    return getWatchEditMode({ allAssets, dialType })
  }

  static getDisplayComponentKey = ({ allAssets, dialType, path, menu, parentKey, componentData }) => {
    return getEditableDisplayComponentKey({ allAssets, dialType, path, menu, parentKey, componentData })
  }

  static getRenderValue = ({ allAssets, dialType, path, menu, parentKey, componentData }) => {
    const mode = EditableComponent.getWatchPreviewMode({ allAssets, dialType, path })
    return mode === WATCH_EDIT_MODE.RUNTIME
      ? EditableComponent.getDisplayComponentKey({ allAssets, dialType, path, menu, parentKey, componentData })
      : ''
  }

  static getSelectStateImage = ({ allData, allAssets, parentKey, dialType }) => {
    const path = parentKey.split('_')
    const isSelected = getSelectedEditableParentKey() === parentKey
    if (!isSelected) return null

    const selectState = allAssets.getIn([dialType, ...path, 'selectState']) || 'select'
    const component = immutableSelector(allData.getIn([...path]))
    const { rectangle = Immutable.Map(), select_image, unselect_image } = immutableSelector(component)
    const { position } = immutableSelector(rectangle)
    const { x, y } = immutableSelector(position)
    const texture = selectState === 'unselect' ? unselect_image : select_image
    if (!texture) return null

    return {
      key: 'selectStateImage',
      texture,
      left: x,
      top: y,
      zIndex: 20004
    }
  }

  static getTagImage = ({ allData, parentKey }) => {
    const path = parentKey.split('_')
    // Previews belong to every editable component; the tag marks only the tree selection.
    if (getSelectedEditableParentKey() !== parentKey) return null

    const component = immutableSelector(allData.getIn([...path]))
    const { text_tag = Immutable.Map() } = immutableSelector(component)
    const { position: textPosition, image: textImage } = immutableSelector(text_tag)
    const { x, y } = immutableSelector(textPosition)

    if (textImage) {
      return {
        key: 'tagImage',
        texture: textImage,
        left: x,
        top: y,
        zIndex: 20000
      }
    }
    return null
  }

  static getComponentPreviewImages = ({ allData, allAssets, parentKey, dialType }) => {
    const path = parentKey.split('_')
    const componentKey = EditableComponent.getDisplayComponentKey({
      allAssets,
      dialType,
      path,
      parentKey,
      componentData: allData.getIn([...path])
    })

    if (!componentKey) return []

    const mode = EditableComponent.getWatchPreviewMode({ allAssets, dialType })
    const component = immutableSelector(allData.getIn([...path]))
    const { rectangle = Immutable.Map(), props = Immutable.Map() } = immutableSelector(component)
    const { position } = immutableSelector(rectangle)
    const { x, y } = immutableSelector(position)
    const images = []

    // Edit mode shows previews so designers can see each data type on the watch face.
    if (mode === WATCH_EDIT_MODE.EDIT) {
      const bg = props.getIn([componentKey, 'bg'])
      const { image: bgImage, position: bgPosition } = immutableSelector(bg)
      const { x: bgPositionX, y: bgPositionY } = immutableSelector(bgPosition)
      if (bgImage) {
        images.push({
          texture: bgImage,
          key: 'bgImage',
          left: bgPositionX,
          top: bgPositionY,
          zIndex: 20002
        })
      }

      const previews = props.getIn([componentKey, 'previews'])
      const previewImage = Immutable.Map.isMap(previews) || Immutable.List.isList(previews)
        ? previews.getIn([0, 'image', 'url'])
        : null
      if (previewImage) {
        images.push({
          texture: previewImage,
          key: 'previewImage',
          left: x,
          top: y,
          zIndex: 20003
        })
      }
    }

    return images
  }

  // handleChangeChildProps = (childPath, item) => {
  //     const [ parent, child ] = childPath
  //     const { parentKey, onChange, allData, layerConfig } = this.props
  //     const path = parentKey.split('_')
  //     const { checked } = immutableSelector(item)
  //     let newAllData = allData
  //     if (checked) {
  //         newAllData = newAllData.setIn([...path, 'props', parent, 'children', child], layerConfig.getIn([parent, 'children', child]))
  //     } else {
  //         newAllData = newAllData.deleteIn([...path, 'props', parent, 'children', child])
  //     }
  //     onChange([], newAllData)
  // }

  handleInsertImage = (img, option) => {
    const { onChange, allData, parentKey } = this.props
    let newAllData = allData
    const path = parentKey.split('_')
    if (img && img.image) {
      newAllData = newAllData.setIn([...path, ...option], img.image)
      // Persist tag dimensions on upload so its label text can be centered.
      if (option[0] === 'text_tag' && img.width && img.height) {
        newAllData = newAllData.setIn([...path, 'text_tag', 'width'], img.width)
        newAllData = newAllData.setIn([...path, 'text_tag', 'height'], img.height)
        newAllData = newAllData.setIn([...path, 'text_display_width'], img.width)
        newAllData = newAllData.setIn([...path, 'text_margin'], 10)
      }
      // Populate the selection rectangle dimensions from the uploaded image.
      if ((option[0] === 'select_image' || option[0] === 'unselect_image') && img.width && img.height) {
        newAllData = newAllData.setIn([...path, 'rectangle', 'width'], img.width)
        newAllData = newAllData.setIn([...path, 'rectangle', 'height'], img.height)
      }
    } else {
      newAllData = newAllData.setIn([...path, ...option], '')
    }
    onChange([], newAllData)
  }

  handleInsertPreviewImage = (img, key) => {
    const { onChange, allData, parentKey } = this.props
    let newAllData = allData
    const path = parentKey.split('_')
    const previewsPath = [...path, 'props', key, 'previews']

    // Guard against null previews — Immutable.setIn fails on null values
    if (newAllData.getIn(previewsPath) == null) {
      newAllData = newAllData.setIn(previewsPath, Immutable.List())
    }

    if (img && img.image) {
      newAllData = newAllData.setIn([...previewsPath, 0, 'image'], {
        url: img.image,
        name: '',
        width: img.width,
        height: img.height
      })
    } else {
      newAllData = newAllData.setIn([...previewsPath, 0, 'image'], { url: '', name: '', width: 0, height: 0 })
    }
    onChange([], newAllData)
  }

  handleChange = (valuePath, value) => {
    const { onChange, allData, parentKey } = this.props
    let newAllData = allData
    const path = parentKey.split('_')
    newAllData = newAllData.setIn([...path, ...valuePath], value)
    onChange([], newAllData)
  }

  handleAssetsChange = (changePath, value) => {
    const { onAssetsChange, allAssets, dialType, parentKey } = this.props
    let newAssets = allAssets

    const path = parentKey.split('_')
    newAssets = newAssets.setIn([dialType, ...path, ...changePath], value)
    onAssetsChange([], newAssets)
  }

  handleDisplayComponentChange = (value) => {
    const { onAssetsChange, allAssets, dialType, parentKey } = this.props
    let newAssets = allAssets
    const path = parentKey.split('_')
    newAssets = newAssets.setIn([dialType, ...path, 'value'], value)
    newAssets = newAssets.setIn([dialType, WATCH_EDIT_MODE_KEY], WATCH_EDIT_MODE.EDIT)
    onAssetsChange([], newAssets)
  }

  getOptions = () => {
    const { menu, parentKey } = this.props
    const componentMenu = menu.find((item) => item.get('key').includes(parentKey))
    if (!componentMenu) return []
    return componentMenu
      .get('children')
      .filter((item) => item.get('checked') === true)
      .toJS()
  }

  handleShowSelectListVisibleChange = (e) => {
    const visible = e.target.value
    const { onChange, allData, parentKey } = this.props
    let newAllData = allData
    const path = parentKey.split('_')
    const selectListPath = [...path, 'select_list']
    const defaultConfig = Immutable.Map({
      title_font_size: 34,
      title_align_h: 'center',
      list_item_vspace: 8,
      list_tips_text_font_size: 32,
      list_tips_text_align_h: 'left',
    })

    if (visible) {
      newAllData = newAllData.setIn(selectListPath, defaultConfig)
    } else {
      newAllData = newAllData.deleteIn(selectListPath)
    }

    onChange([], newAllData)

    this.setState({
      showSelectListVisible: visible
    })
  }

  preloadTextTagDimensions() {
    const { allData, parentKey, onChange } = this.props
    const path = parentKey.split('_')
    const textTag = allData.getIn([...path, 'text_tag']) || Immutable.Map()
    const image = textTag.get('image')
    const height = textTag.get('height')
    if (image && !height) {
      const img = new window.Image()
      img.onload = () => {
        const newData = allData
          .setIn([...path, 'text_tag', 'width'], img.naturalWidth)
          .setIn([...path, 'text_tag', 'height'], img.naturalHeight)
          .setIn([...path, 'text_display_width'], img.naturalWidth)
          .setIn([...path, 'text_margin'], 10)
        onChange([], newData)
      }
      img.src = image
    }
  }

  preloadSelectImageDimensions() {
    const { allData, parentKey, onChange } = this.props
    const path = parentKey.split('_')
    const rectangle = allData.getIn([...path, 'rectangle']) || Immutable.Map()
    const width = rectangle.get('width')
    const selectImage = allData.getIn([...path, 'select_image'])
    // Populate the rectangle dimensions only when an image exists and dimensions are unset.
    if (!width && selectImage) {
      const img = new window.Image()
      img.onload = () => {
        const newData = allData
          .setIn([...path, 'rectangle', 'width'], img.naturalWidth)
          .setIn([...path, 'rectangle', 'height'], img.naturalHeight)
        onChange([], newData)
      }
      img.src = selectImage
    }
  }


  render() {
    const { showSelectListVisible } = this.state
    const { allData, allAssets, watchConfig, parentKey, dialType, menu } = this.props
    const path = parentKey.split('_')
    const options = this.getOptions()
    const displayComponentKey = EditableComponent.getDisplayComponentKey({
      allAssets,
      dialType,
      path,
      menu,
      parentKey,
      componentData: allData.getIn([...path])
    })
    const defaultShow = allAssets.getIn([dialType, ...path, 'defaultShow'])
    const selectState = allAssets.getIn([dialType, ...path, 'selectState']) || 'select'
    const { screenWidth, screenHeight } = immutableSelector(watchConfig)
    const component = immutableSelector(allData.getIn([...path]))
    const { rectangle = Immutable.Map(), select_image, text_tag = Immutable.Map(), unselect_image, props = Immutable.Map() } = immutableSelector(
      component
    )
    const { position, width, height } = immutableSelector(rectangle)
    const { x, y } = immutableSelector(position)
    const { position: textPosition, image: textImage } = immutableSelector(text_tag)
    const { x: textX, y: textY } = immutableSelector(textPosition)

    return (
      <>
        <div className="editable-component-tutorial">
          <a
            href="https://docs.zepp.com/docs/guides/tools/watchface/guides/editable-component/"
            target="_blank"
            rel="noreferrer"
          >
            <T id="editable_component_tutorial" />
          </a>
        </div>
        <div className="edit-mode-hint">
          <InfoCircleOutlined className="edit-mode-hint-icon" />
          <T id="edit_mode_config_hint" />
        </div>
        <Collapse
          bordered={false}
          defaultActiveKey={['basic']}
          className="editable-component-config"
        >
        <Panel header={<span data-testid="editable-component-basic-panel-title"><T id="editable_component_basic_config" /></span>} key="basic">
          <div data-testid="editable-component-basic-panel">
          <div className="name">
            <T id="select_state_bg" /><span className="optional-tag"><T id="optional" /></span>
          </div>
          <div className="content">
            <div className="item">
              <div className="col col-v">
                <Upload
                  key={select_image}
                  fileCountLimit={1}
                  maxWidth={screenWidth}
                  maxHeight={screenHeight}
                  field={['select_image']}
                  fileList={select_image ? [{ image: select_image }] : []}
                  onChange={this.handleInsertImage}
                />
              </div>
            </div>
          </div>
          <div className="name">
            <T id="unselect_state_bg" /><span className="optional-tag"><T id="optional" /></span>
          </div>
          <div className="content">
            <div className="item">
              <div className="col col-v">
                <Upload
                  fileCountLimit={1}
                  maxWidth={screenWidth}
                  maxHeight={screenHeight}
                  field={['unselect_image']}
                  fileList={unselect_image ? [{ image: unselect_image }] : []}
                  onChange={this.handleInsertImage}
                />
              </div>
            </div>
          </div>
          <div className="name">
            <T id="state_preview_label" />
          </div>
          <div className="content">
            <div className="item">
              <Select
                dropdownClassName="watch-skin-select-dropdown"
                style={SELECT_STYLE}
                value={selectState}
                onChange={(value) => {
                  this.handleAssetsChange(['selectState'], value)
                }}
              >
                <Option key="select" value="select">
                  <T id="selected" />
                </Option>
                <Option key="unselect" value="unselect">
                  <T id="not_selected" />
                </Option>
              </Select>
            </div>
          </div>
          <div className="name">
            <T id="touch_area_position" />
          </div>
          <div className="content">
            <div className="item">
              <div className="col">
                <div className="key">x</div>
                <InputNumber
                  min={0}
                  max={screenWidth}
                  value={x}
                  onChange={(value) => {
                    this.handleChange(['rectangle', 'position', 'x'], value)
                  }}
                />
              </div>
              <div className="col">
                <div className="key">y</div>
                <InputNumber
                  min={0}
                  max={screenHeight}
                  value={y}
                  onChange={(value) => {
                    this.handleChange(['rectangle', 'position', 'y'], value)
                  }}
                />
              </div>
            </div>
          </div>
          <div className="name">
            <T id="touch_area_size" />
          </div>
          <div className="content">
            <div className="item">
              <div className="col">
                <div className="key">W</div>
                <InputNumber
                  min={0}
                  max={screenWidth}
                  value={width}
                  onChange={(value) => {
                    this.handleChange(['rectangle', 'width'], value)
                  }}
                />
              </div>
              <div className="col">
                <div className="key">H</div>
                <InputNumber
                  min={0}
                  max={screenHeight}
                  value={height}
                  onChange={(value) => {
                    this.handleChange(['rectangle', 'height'], value)
                  }}
                />
              </div>
            </div>
          </div>
          <div className="name" data-testid="editable-component-enabled-items-title">
            <T id="optional_types_label" />
          </div>
          <div className="content">
            <div className="item enabled-data-list">
              {options.length ? (
                options.map((item) => {
                  const isActive = displayComponentKey === item.key
                  return (
                    <Tag
                      key={item.key}
                      color={isActive ? 'blue' : undefined}
                      className="enabled-data-tag"
                      onClick={() => this.handleDisplayComponentChange(item.key)}
                    >
                      <T id={item.name} />
                    </Tag>
                  )
                })
              ) : (
                <span className="empty-enabled-data"><T id="no_optional_types" /></span>
              )}
            </div>
          </div>
          <div className="config-tip" data-testid="editable-component-enabled-items-tip">
            <T id="optional_types_tip" />
          </div>
          <div className="name">
            <T id="tips_bg_label" /><span className="optional-tag"><T id="optional" /></span>
          </div>
          <div className="content">
            <div className="item">
              <div className="col col-v">
                <Upload
                  key={textImage}
                  fileCountLimit={1}
                  maxWidth={screenWidth}
                  maxHeight={screenHeight}
                  field={['text_tag', 'image']}
                  fileList={textImage ? [{ image: textImage }] : []}
                  onChange={this.handleInsertImage}
                />
              </div>
            </div>
          </div>
          <div className="name">
            <span><T id="tips_position_label" /></span>
          </div>
          <div className="content">
            <div className="item">
              <div className="col">
                <div className="key">x</div>
                <InputNumber
                  min={0}
                  max={screenWidth}
                  value={textX}
                  onChange={(value) => {
                    this.handleChange(['text_tag', 'position', 'x'], value)
                  }}
                />
              </div>
              <div className="col">
                <div className="key">y</div>
                <InputNumber
                  min={0}
                  max={screenHeight}
                  value={textY}
                  onChange={(value) => {
                    this.handleChange(['text_tag', 'position', 'y'], value)
                  }}
                />
              </div>
            </div>
          </div>
          <Collapse bordered={false} className="editable-component-resource-config" defaultActiveKey={['preview-resource']}>
            <Panel header={<span data-testid="editable-component-preview-resource-title"><span className="required-mark">*</span><T id="data_preview_optional" /></span>} key="preview-resource">
              {options.length === 0 ? (
                <div className="config-tip" style={{ marginBottom: 12 }}>
                  <T id="select_left_data_items_tip" />
                </div>
              ) : null}
              {options.map((option) => {
                const { name, key } = option
                const item = props.get(key) || Immutable.Map()
                const { previews: rawPreviews } = immutableSelector(item)
                const previews = rawPreviews != null ? rawPreviews : Immutable.Map()
                const previewImage = previews.getIn([0, 'image', 'url']) || ''

                return (
                  <div key={key}>
                    <div className="name">
                      <span className="required-mark">*</span><T id="preview_image_with_name" values={{ name: <T id={name} /> }} />
                    </div>
                    <div className="content">
                      <div className="item">
                        <div className="col col-v">
                          <Upload
                            key={previewImage}
                            fileCountLimit={1}
                            maxWidth={screenWidth}
                            maxHeight={screenHeight}
                            field={key}
                            fileList={previewImage ? [{ image: previewImage }] : []}
                            onChange={this.handleInsertPreviewImage}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </Panel>
          </Collapse>
          </div>
        </Panel>
        <Panel header={<span data-testid="editable-component-watch-display-panel-title"><T id="editable_component_watch_display_config" /></span>} key="watch-display">
          <div className="editable-component-watch-display-config" data-testid="editable-component-watch-display-panel">
            <div className="name">
              <T id="default_type_label" />
            </div>
            <div className="content">
              <div className="item">
                <Select
                  dropdownClassName="watch-skin-select-dropdown"
                  style={SELECT_STYLE}
                  value={options.length && options.find((item) => item.key === defaultShow) ? defaultShow : undefined}
                  onChange={(value) => {
                    this.handleAssetsChange(['defaultShow'], value)
                  }}
                >
                  {options.map((item) => {
                    const { name, key } = item
                    return (
                      <Option key={key} value={key}>
                        <T id={name} />
                      </Option>
                    )
                  })}
                </Select>
              </div>
            </div>
            <div className="item">
              <T id="select_list_label" /><Tooltip title={<p><T id="select_list_tip"/></p>} color={TOOLTIP_COLOR}><QuestionCircleOutlined style={{ marginLeft: '6px' }}/></Tooltip>
            </div>
            <div className="content">
              <div className="item">
                <Radio.Group
                  onChange={this.handleShowSelectListVisibleChange}
                  value={showSelectListVisible}
                >
                  <Radio value>
                    <T id="yes" />
                  </Radio>
                  <Radio value={false}>
                    <T id="no" />
                  </Radio>
                </Radio.Group>
              </div>
            </div>
            {/* SelectList uses built-in defaults; select_list in config is for JS export */}
          </div>
        </Panel>
      </Collapse>
      </>
    )
  }
}

export default EditableComponent
